import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { WorkspaceView } from './nav';

interface SidebarRoleSwitcherProps {
  view: WorkspaceView;
  onChange: (next: WorkspaceView) => void;
}

/**
 * Internal-staff workspace switcher — a dark-sidebar segmented pill under the
 * logo that toggles the whole shell between the Administrator and Manager
 * workspaces. Rendered by {@link Sidebar} only for users holding BOTH roles
 * (see `isDualRole`); the role is otherwise server-authoritative from `whoami`.
 * Mirrors the prototype `SidebarRoleSwitcher` (appshell/TopBar.jsx).
 */
export function SidebarRoleSwitcher({ view, onChange }: SidebarRoleSwitcherProps) {
  const { t } = useTranslation();
  const options: { id: WorkspaceView; label: string }[] = [
    { id: 'admin', label: t('roleSwitcher.admin') },
    { id: 'manager', label: t('roleSwitcher.manager') },
  ];

  return (
    <Box
      role="group"
      aria-label={t('roleSwitcher.aria')}
      sx={{
        m: '2px 16px 12px',
        p: 0.5,
        display: 'flex',
        gap: '3px',
        bgcolor: 'rgba(255, 255, 255, 0.06)',
        borderRadius: 2,
      }}
    >
      {options.map((option) => {
        const selected = view === option.id;
        return (
          <Box
            component="button"
            type="button"
            key={option.id}
            aria-pressed={selected}
            onClick={() => onChange(option.id)}
            sx={{
              flex: 1,
              py: '7px',
              px: 1,
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              fontFamily: (theme) => theme.typography.button.fontFamily,
              fontSize: 12,
              lineHeight: 1,
              fontWeight: selected ? 600 : 500,
              bgcolor: selected ? 'brand.sidebarActiveBg' : 'transparent',
              color: selected ? '#fff' : 'rgba(255, 255, 255, 0.72)',
              transition: 'background 120ms',
            }}
          >
            {option.label}
          </Box>
        );
      })}
    </Box>
  );
}
